import { v4 } from 'uuid';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import {
	AgentCreationStep,
	AgentTrainingStep,
	type AgentTrainingStoreActions,
	type AgentTrainingStoreState,
	TScrapingStatus
} from '@/types/agent-training';
import { buildScrapingSteps } from '@/utils/buildScrapingSteps.ts';

const initialState: AgentTrainingStoreState = {
	partner_idn: null,
	member_idn: null,
	actor: null,
	hasUnreadNotification: false,
	step: AgentCreationStep.SOURCE_SELECT,
	agentTrainingStep: AgentTrainingStep.Onboarding,
	isCustomerFormAllowed: false,
	isContactFormAllowed: false,
	isNextButtonClicked: false,
	isModalOpenedFlag: false,
	scrapingStatus: TScrapingStatus.INITIAL,
	isScrappingAnimationFinished: false,
	isScrappingSuccessAnimationFinished: false,
	isAgentProgressStepsVisible: false,
	agentSetupProgressStep: 0,
	isMiniModeExpanded: true,
	miniModeDragX: null,
	scrapingSteps: Array.from({ length: 9 }, () => ({
		id: v4(),
		question: '',
		answer: '',
		isLoading: true
	}))
};
export const useAgentTraining = create<
	AgentTrainingStoreState & AgentTrainingStoreActions
>()(
	persist(
		(set) => ({
			step: AgentCreationStep.SOURCE_SELECT,
			agentTrainingStep: AgentTrainingStep.Onboarding,
			isCustomerFormAllowed: false,
			hasUnreadNotification: false,
			partner_idn: null,
			member_idn: null,
			isModalOpenedFlag: false,
			scrapingStatus: TScrapingStatus.INITIAL,
			isNextButtonClicked: false,
			agentSetupProgressStep: 0,
			isScrappingAnimationFinished: false,
			actor: null,
			isScrappingSuccessAnimationFinished: false,
			scrapingSteps: Array.from({ length: 9 }, () => ({
				id: v4(),
				question: '',
				answer: '',
				isLoading: true
			})),
			isContactFormAllowed: false,
			isAgentProgressStepsVisible: false,
			isMiniModeExpanded: true,
			miniModeDragX: null,

			setAgentProgressSteps: (stepsCount) => {
				set({ agentSetupProgressStep: stepsCount });
			},
			setPartnerIdn: (partner_idn) => {
				set({ partner_idn });
			},
			setMemberIdn: (member_idn) => {
				set({ member_idn });
			},
			setActor: (actor) => {
				set({ actor });
			},
			toggleMiniExpanded: () => {
				set((state) => ({
					isMiniModeExpanded: !state.isMiniModeExpanded
				}));
			},

			setMiniModeDragX: (x) => {
				set({
					miniModeDragX: x
				});
			},

			setNotification: (val) => {
				set({ hasUnreadNotification: val });
			},
			resetState: () => {
				set(initialState);
			},
			resetStatePreservePartner: () =>
				set((state) => ({
					...initialState,
					partner_idn: state.partner_idn,
					member_idn: state.member_idn
				})),

			setIsNextButtonClicked: () => {
				set({ isNextButtonClicked: true });
			},
			incrementAgentProgressStep: (max: number) =>
				set((state) => ({
					agentSetupProgressStep:
						state.agentSetupProgressStep < max
							? state.agentSetupProgressStep + 1
							: state.agentSetupProgressStep
				})),
			setAgentProgressStepsVisible: (visible) => {
				set({ isAgentProgressStepsVisible: visible });
			},
			setIsContactFormAllowed: (allowed) => {
				set({ isContactFormAllowed: allowed });
			},
			setScrapingStep: (step, initialLoadingCount = 9, cols = 3) => {
				set((state) => ({
					scrapingSteps: buildScrapingSteps(
						state.scrapingSteps ?? [],
						step,
						initialLoadingCount,
						cols
					)
				}));
			},

			setAgentStep: (step) => {
				set({ step });
			},
			setTrainingStep: (step) => {
				set({ agentTrainingStep: step });
			},
			setShowCustomerForm: (isVisible) => {
				set({
					isCustomerFormAllowed: isVisible
				});
			},
			setModalOpenedFlag: (isOpen) => {
				set({ isModalOpenedFlag: isOpen });
			},

			setScrapingStatus: (value: TScrapingStatus) => {
				set({ scrapingStatus: value });
			},

			setIsScrappingAnimationFinished: (value: boolean) => {
				set({ isScrappingAnimationFinished: value });
			},

			setIsScrappingSuccessAnimationFinished: (value: boolean) => {
				set({ isScrappingSuccessAnimationFinished: value });
			}
		}),
		{
			name: 'agent-training-storage',
			storage: createJSONStorage(() => globalThis.localStorage),
			partialize: ({ actor, ...state }) => state
		}
	)
);

export type CustomerInitialValues = {
	customer_phone: string;
	customer_name: string;
	customer_email: string;
};
